// Quack, this is a duck :) https://github.com/erikras/ducks-modular-redux
import { combineReducers } from 'redux';
import { get, post } from '../../shared/http/request';
import { handleApiError } from '../../shared/http/error';

/** ************************************
 * Abnormalitiy analysis redux bundle *
 ************************************* */
// API
const BASE_URL = '/api/abnormalities/';

// Actions
const SILENT_LOAD_ABNORMALITY_ANALYSIS_PENDING = 'Fetching abnormality analysis silently';
const LOAD_ABNORMALITY_ANALYSIS_PENDING = 'Fetching abnormality analysis';
const LOAD_ABNORMALITY_ANALYSIS_FULFILLED = 'Abnormality analysis fetched';
const LOAD_ABNORMALITY_ANALYSIS_REJECTED = 'Failed to fetch abnormality analysis';
const POST_UNRELATED_MANIFESTS_ANALYSIS_FULFILLED = 'POST_UNRELATED_MANIFESTS_ANALYSIS_FULFILLED';
const POST_UNRELATED_MANIFESTS_ANALYSIS_PENDING = 'POST_UNRELATED_MANIFESTS_ANALYSIS_PENDING';
const POST_UNRELATED_MANIFESTS_ANALYSIS_REJECTED = 'POST_UNRELATED_MANIFESTS_ANALYSIS_REJECTED';

const POST_COLLECTION_ROUTES_PENDING = 'POST_COLLECTION_ROUTES_PENDING';
const POST_COLLECTION_ROUTES_FULFILLED = 'POST_COLLECTION_ROUTES_FULFILLED';
const POST_COLLECTION_ROUTES_REJECTED = 'POST_COLLECTION_ROUTES_REJECTED';

const LINK_EMA_SMA_PENDING = 'Linking EMA/SMA pending';
const LINK_EMA_SMA_FULFILLED = 'EMA/SMA linked';
const LINK_EMA_SMA_REJECTED = 'Failed to link EMA/SMA';
const UNLINK_EMA_SMA_PENDING = 'Unlinking EMA/SMA pending';
const UNLINK_EMA_SMA_FULFILLED = 'EMA/SMA unlinked';
const UNLINK_EMA_SMA_REJECTED = 'Failed to unlink EMA/SMA';

const SAVE_JBA_DATA = 'Saved analysis data for journey builder';
const CLEAR_JBA_DATA = 'Cleared analysis data for journey builder';

// Reducers
const entities = (state = null, action) => {
    switch (action.type) {
        case LOAD_ABNORMALITY_ANALYSIS_FULFILLED:
            const abnormalityAnalysis = [
                {
                    [action.abnormalityAnalysis.abnormalityId]: action.abnormalityAnalysis,
                },
            ];
            return Object.assign({}, state, ...abnormalityAnalysis);
        case POST_UNRELATED_MANIFESTS_ANALYSIS_FULFILLED:
            const newAbnormalityAnalysis = [
                {
                    [action.abnormalityAnalysis.abnormalityId]: action.abnormalityAnalysis,
                },
            ];
            return Object.assign({}, state, ...newAbnormalityAnalysis);
        default:
            return state;
    }
};

const journeyBuilderAnalysisData = (state = {}, action) => {
    switch (action.type) {
        case SAVE_JBA_DATA:
            return Object.assign({}, action.data);
        case CLEAR_JBA_DATA:
            return {};
        default:
            return state;
    }
};

const isSilentlyFetching = (state = false, action) => {
    switch (action.type) {
        case SILENT_LOAD_ABNORMALITY_ANALYSIS_PENDING:
            return true;
        case LOAD_ABNORMALITY_ANALYSIS_FULFILLED:
        case LOAD_ABNORMALITY_ANALYSIS_REJECTED:
        case POST_UNRELATED_MANIFESTS_ANALYSIS_FULFILLED:
        case POST_UNRELATED_MANIFESTS_ANALYSIS_REJECTED:
            return false;
        default:
            return state;
    }
};

const isFetching = (state = false, action) => {
    switch (action.type) {
        case LOAD_ABNORMALITY_ANALYSIS_PENDING:
        case POST_UNRELATED_MANIFESTS_ANALYSIS_PENDING:
            return true;
        case LOAD_ABNORMALITY_ANALYSIS_FULFILLED:
        case LOAD_ABNORMALITY_ANALYSIS_REJECTED:
        case POST_UNRELATED_MANIFESTS_ANALYSIS_FULFILLED:
        case POST_UNRELATED_MANIFESTS_ANALYSIS_REJECTED:
            return false;
        default:
            return state;
    }
};

const error = (state = null, action) => {
    switch (action.type) {
        case LOAD_ABNORMALITY_ANALYSIS_REJECTED:
        case POST_UNRELATED_MANIFESTS_ANALYSIS_REJECTED:
            return true;
        case LOAD_ABNORMALITY_ANALYSIS_PENDING:
        case LOAD_ABNORMALITY_ANALYSIS_FULFILLED:
        case POST_UNRELATED_MANIFESTS_ANALYSIS_PENDING:
        case POST_UNRELATED_MANIFESTS_ANALYSIS_FULFILLED:
            return false;
        default:
            return state;
    }
};

export default combineReducers({
    entities,
    journeyBuilderAnalysisData,
    isFetching,
    isSilentlyFetching,
    error,
});

// Selectors
export const getAbnormalityAnalysis = (state, id) =>
    state.entities.abnormalityAnalysis.entities
        ? state.entities.abnormalityAnalysis.entities[id]
        : null;
export const getJourneyBuilderAnalysisData = state =>
    state.entities.abnormalityAnalysis
        ? state.entities.abnormalityAnalysis.journeyBuilderAnalysisData
        : null;
export const getIsFetching = state => state.entities.abnormalityAnalysis.isFetching;
export const getIsSilentlyFetching = state => state.entities.abnormalityAnalysis.isSilentlyFetching;
export const getError = state => state.entities.abnormalityAnalysis.error;

// Action Creators/Thunks
export function loadAbnormalityAnalysis(abnormalityId) {
    return function(dispatch) {
        dispatch({ type: LOAD_ABNORMALITY_ANALYSIS_PENDING });

        return get(`${BASE_URL}${abnormalityId}/analysis`).then(
            response =>
                dispatch({
                    type: LOAD_ABNORMALITY_ANALYSIS_FULFILLED,
                    abnormalityAnalysis: response,
                }),
            error =>
                handleApiError(error, dispatch, {
                    type: LOAD_ABNORMALITY_ANALYSIS_REJECTED,
                    message: error.message,
                })
        );
    };
}

export function silentFetchAbnormalityAnalysis(abnormalityId) {
    return function(dispatch) {
        dispatch({ type: SILENT_LOAD_ABNORMALITY_ANALYSIS_PENDING });

        return get(`${BASE_URL}${abnormalityId}/analysis`).then(
            response =>
                setTimeout(
                    () =>
                        dispatch({
                            type: LOAD_ABNORMALITY_ANALYSIS_FULFILLED,
                            abnormalityAnalysis: response,
                        }),
                    1500
                ),
            error =>
                handleApiError(error, dispatch, {
                    type: LOAD_ABNORMALITY_ANALYSIS_REJECTED,
                    message: error.message,
                })
        );
    };
}

export function linkSmaEma(manifestId, emaSmaManifestId) {
    return function(dispatch) {
        dispatch({ type: LINK_EMA_SMA_PENDING });
        return post('/api/command/attachEmaSmaManifestCommand', {
            manifestId,
            emaSmaManifestId,
        }).then(
            () => dispatch({ type: LINK_EMA_SMA_FULFILLED }),
            error =>
                handleApiError(error, dispatch, {
                    type: LINK_EMA_SMA_REJECTED,
                    message: error.message,
                })
        );
    };
}

export function unlinkSmaEma(manifestId) {
    return function(dispatch) {
        dispatch({ type: UNLINK_EMA_SMA_PENDING });
        return post('/api/command/detachEmaSmaManifestCommand', {
            manifestId,
        }).then(
            () => dispatch({ type: UNLINK_EMA_SMA_FULFILLED }),
            error =>
                handleApiError(error, dispatch, {
                    type: UNLINK_EMA_SMA_REJECTED,
                    message: error.message,
                })
        );
    };
}

export function postUnrelatedManifestsAnalysis(abnormalityId, body) {
    return function(dispatch) {
        dispatch({ type: POST_UNRELATED_MANIFESTS_ANALYSIS_PENDING });

        return post(
            `${BASE_URL}${abnormalityId}/analysis?includeOriginalManifestTimeLines=true`,
            body
        ).then(
            response =>
                dispatch({
                    type: POST_UNRELATED_MANIFESTS_ANALYSIS_FULFILLED,
                    abnormalityAnalysis: response,
                }),
            error =>
                handleApiError(error, dispatch, {
                    type: POST_UNRELATED_MANIFESTS_ANALYSIS_REJECTED,
                    message: error.message,
                })
        );
    };
}

export function loadCollectionRoutes(body) {
    return function(dispatch) {
        dispatch({
            POST_COLLECTION_ROUTES_PENDING,
        });

        return post('/abnormality/manifests/collection-routes', body).then(
            response =>
                dispatch({
                    type: POST_COLLECTION_ROUTES_FULFILLED,
                    collectionRequestBody: body,
                    collectionRoutes: response,
                }),
            error =>
                dispatch({
                    type: POST_COLLECTION_ROUTES_REJECTED,
                    error,
                })
        );
    };
}

export function saveJourneyBuilderAnalysisData(data) {
    return function(dispatch) {
        dispatch({
            type: SAVE_JBA_DATA,
            data,
        });
    };
}

export function clearJourneyBuilderAnalysisData() {
    return function(dispatch) {
        dispatch({
            type: CLEAR_JBA_DATA,
        });
    };
}
